import { BOARD_SCHEMA_VERSION, TILE_SCHEMA_VERSION } from './constants.js';
import { BoardSchema, type Board } from './board.js';

/**
 * Forward-only migration ladder (ADR-002 §6.3).
 *
 * Boards live in localStorage for MONTHS and will outlive several schema
 * revisions (ADR-001). We assume the schema WILL change and wire the ladder now,
 * so adding v2 later is a ONE-ENTRY change — append a migration fn and bump the
 * version constant. Nothing else moves.
 *
 * Pipeline on every load/import (ORDER MATTERS): read stamped version → run each
 * migration in sequence up to CURRENT → THEN Zod-validate. Migrate, then validate.
 *
 * Each migration is a pure function taking the raw (already version-N) object and
 * returning a version-(N+1) object. It must not throw on well-formed prior data;
 * it produces fresh objects (never mutates input).
 */

/** A single forward step, N -> N+1, over the opaque board blob (pre-validation). */
export type BoardMigration = (raw: Record<string, unknown>) => Record<string, unknown>;

/**
 * Board-envelope migrations, indexed by SOURCE version. `BOARD_MIGRATIONS[n]`
 * migrates a version-`n` board to version-`n+1`.
 *
 * v1 is the current version, so the ladder is empty today. To add v2:
 *   1. write `migrate1to2(raw)` (pure, adds/renames/drops fields with safe defaults)
 *   2. set `BOARD_MIGRATIONS[1] = migrate1to2`
 *   3. bump `BOARD_SCHEMA_VERSION` to 2 in constants.ts
 * The loader picks it up automatically.
 */
export const BOARD_MIGRATIONS: Readonly<Record<number, BoardMigration>> = {
  // 1: migrate1to2,   // <- appended here when v2 lands
};

/**
 * Per-tile migrations, indexed by SOURCE version, mirroring the board ladder.
 * Two version axes evolve independently (ADR-002 §6.1): a new weather field bumps
 * TILE_SCHEMA_VERSION without a board-wide bump. Empty today (v1 identity).
 */
export type TileMigration = (raw: Record<string, unknown>) => Record<string, unknown>;
export const TILE_MIGRATIONS: Readonly<Record<number, TileMigration>> = {
  // 1: migrateTile1to2,
};

/** Result of a load attempt — a discriminated union, never an exception (§6). */
export type LoadResult =
  | { readonly ok: true; readonly board: Board }
  | { readonly ok: false; readonly reason: LoadFailure; readonly detail?: string };

export type LoadFailure =
  /** Stored version > app CURRENT: a newer build wrote this. Do not mangle (§6.3). */
  | 'future-version'
  /** Not board-shaped, forged/missing version, or failed post-migration validation. */
  | 'invalid';

/** Read the stamped board version from an untrusted blob, if plausibly present. */
function readVersion(raw: unknown): number | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const v = (raw as Record<string, unknown>).schemaVersion;
  return typeof v === 'number' && Number.isInteger(v) && v >= 1 ? v : null;
}

/**
 * Run a migration ladder from `fromVersion` up to `toVersion`, using the given
 * ordered migrations map. Pure — returns a fresh blob, never mutates input.
 * Exported so the ladder MECHANISM is directly testable (prove a v1→v2 hop with a
 * fake migration) without touching the production `BOARD_MIGRATIONS` constant.
 * A gap in the ladder throws (surfaced as `invalid` by the loader — never a silent skip).
 */
export function runMigrations(
  raw: Record<string, unknown>,
  fromVersion: number,
  toVersion: number,
  migrations: Readonly<Record<number, BoardMigration>>,
): Record<string, unknown> {
  let current: Record<string, unknown> = { ...raw };
  for (let v = fromVersion; v < toVersion; v += 1) {
    const step = migrations[v];
    if (!step) {
      throw new Error(`missing migration for v${v}`);
    }
    current = step(current);
  }
  return current;
}

/** Run the production board ladder from `fromVersion` up to CURRENT. */
function runBoardLadder(
  raw: Record<string, unknown>,
  fromVersion: number,
): Record<string, unknown> {
  return runMigrations(raw, fromVersion, BOARD_SCHEMA_VERSION, BOARD_MIGRATIONS);
}

/**
 * Load an untrusted board blob: validate version → migrate → Zod-validate.
 *
 * NEVER throws and NEVER mutates input. Every failure maps to a designed outcome
 * (future-version / invalid), so a corrupt or hostile blob degrades gracefully and
 * the board can quarantine + notify instead of crashing (ADR-002 §4, §6.3).
 *
 * This is the single entry point every import path funnels through — localStorage
 * load, share link, paste, URL param, and (Phase 4) NL-builder output all call this.
 */
export function loadBoard(raw: unknown): LoadResult {
  try {
    const version = readVersion(raw);
    if (version === null) {
      return { ok: false, reason: 'invalid', detail: 'missing or malformed schemaVersion' };
    }
    if (version > BOARD_SCHEMA_VERSION) {
      return { ok: false, reason: 'future-version', detail: `stored v${version} > app v${BOARD_SCHEMA_VERSION}` };
    }
    const migrated = runBoardLadder(raw as Record<string, unknown>, version);
    const parsed = BoardSchema.safeParse(migrated);
    if (!parsed.success) {
      return { ok: false, reason: 'invalid', detail: parsed.error.message };
    }
    return { ok: true, board: parsed.data };
  } catch (err) {
    // Any unexpected error (e.g. ladder gap) becomes a safe `invalid` — never a crash.
    const detail = err instanceof Error ? err.message : 'unknown load error';
    return { ok: false, reason: 'invalid', detail };
  }
}

/** Convenience: the current schema versions, for callers that stamp fresh boards. */
export const CURRENT_VERSIONS = {
  board: BOARD_SCHEMA_VERSION,
  tile: TILE_SCHEMA_VERSION,
} as const;
